import { Icon, Modal } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { DateToShortReadableDate } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SLICES } from '../../../constants/slices';
import { useDeleteClaimMutation } from '../../../data/claim';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { PetIcon } from '../../petIcon/petIcon';
import { ACTION_BUTTON_TYPE } from '../claimDetails/claimStatusMap';
import { useGetPetInfoQuery } from '../../../data/policy';
import './claimSummary.scss';
import { setCustomerClaimId } from '../../../store/slice/claimSlice';

export const ClaimSummary = ({
  icon = 'PetSurePaw',
  includeDescription,
  description,
  profilePic,
  fullPolicyNumber,
  species,
  gender,
  name,
  claimNo,
  receivedDate,
  modified,
  includeCompleteButton,
  includeDeleteButton,
  customerClaimId,
  renderActionButton,
  handleActionButtonClick,
  isUploadAdditionalDocs
}) => {
  const dispatch = useDispatch();
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isV3PenTestRemediationEndpointsEnabled = cspConfiguration?.IsV3PenTestRemediationEndpointsEnabled;
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const { refetch: getPetInfo } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const navigate = useNavigate();

  const [isDialogShowing, setIsDialogShowing] = useState(false);
  const [deleteClaim, { isSuccess }] = useDeleteClaimMutation();
  const formattedDate = DateToShortReadableDate(new Date(receivedDate));

  const handleCompleteClaim = () => {
    dispatch(setCustomerClaimId(customerClaimId));
    navigate(CSPUrlsMap.newClaim.url, {
      state: { mode: 'completeClaim' }
    });
  };
  useEffect(() => {
    if (isSuccess) {
      setIsDialogShowing(false);
      if (includeDeleteButton) {
        getPetInfo();
      }
    }
  }, [isSuccess, includeDeleteButton, getPetInfo]);

  return (
    <>
      {isDialogShowing && (
        <Modal
          customCssClass="delete-claim-dialog"
          size="xsmall"
          showFooterDivider
          open
          onClose={() => setIsDialogShowing(false)}
          header={<h2 className="modal-title">Delete saved claim</h2>}
          footer={
            <div className="footerModalContainer">
              <button
                type="button"
                className="button primary outline"
                onClick={() => setIsDialogShowing(false)}
              >
                <span className="labelText">Cancel</span>
              </button>

              <button
                type="button"
                className="button primary"
                onClick={() =>
                  deleteClaim({
                    claimNo: customerClaimId,
                    auth0Token: auth0AccessToken,
                    isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
                  })
                }
              >
                <span className="labelText">Delete</span>
              </button>
            </div>
          }
        >
          <p className="delete-modal-content">Are you sure you want to delete this saved claim?</p>
        </Modal>
      )}
      <section className="claimDetailItemContainer">
        <div className="claimSummaryPet">
          <PetIcon
            profilePic={profilePic}
            fullPolicyNumber={fullPolicyNumber}
            species={species}
            gender={gender}
          />
          <div>
            <div>
              <h3 className="petNameHeading">
                <Icon icon={icon} />
                {name}
              </h3>
              <div className="claimSummary">
                {claimNo && <span>Claim no. {claimNo}</span>}
                {modified ? <span>Saved {formattedDate}</span> : <span>Submitted {formattedDate}</span>}
              </div>
            </div>
            {includeDescription && <div className="claimDescriptionDesktop">{parse(description)}</div>}
          </div>
        </div>
        {isUploadAdditionalDocs && (
          <button
            type="button"
            onClick={() => handleActionButtonClick(ACTION_BUTTON_TYPE.UPLOAD_DOCUMENTS)}
            className="button primary actionButton"
          >
            <Icon icon="upload-required-docs" />
            Upload documents
          </button>
        )}

        {renderActionButton && renderActionButton({ onClick: (type) => handleActionButtonClick(type) })}

        {includeDescription && <div className="claimDescriptionMobile">{parse(description)}</div>}

        {(includeCompleteButton || includeDeleteButton) && (
          <div className="buttonContainer">
            {includeDeleteButton && (
              <button
                type="button"
                className="button primary outline"
                onClick={() => setIsDialogShowing(true)}
              >
                <span className="labelText">Delete</span>
              </button>
            )}

            {includeCompleteButton && (
              <button
                type="button"
                className="button primary"
                onClick={handleCompleteClaim}
              >
                <span className="labelText">Complete</span>
              </button>
            )}
          </div>
        )}
      </section>
    </>
  );
};

ClaimSummary.propTypes = {
  icon: PropTypes.string,
  includeDescription: PropTypes.bool,
  description: PropTypes.string,
  profilePic: PropTypes.string,
  fullPolicyNumber: PropTypes.string,
  species: PropTypes.oneOf(['Canine', 'Feline']),
  gender: PropTypes.oneOf(['Male', 'Female']),
  name: PropTypes.string,
  claimNo: PropTypes.string,
  receivedDate: PropTypes.string,
  modified: PropTypes.string,
  includeCompleteButton: PropTypes.bool,
  includeDeleteButton: PropTypes.bool,
  customerClaimId: PropTypes.number,
  renderActionButton: PropTypes.element,
  handleActionButtonClick: PropTypes.func,
  isUploadAdditionalDocs: PropTypes.bool
};
